import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Home, Building, TreePine, Key, Bot, RefreshCw, Zap, Calculator, Target, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealPipeline = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(type || 'land');

  const dealTypes = [
    { id: 'land', label: 'Land Deals', icon: TreePine, color: 'text-green-400' },
    { id: 'section8', label: 'Section 8', icon: Key, color: 'text-blue-400' },
    { id: 'sfh', label: 'Wholesale SFH', icon: Home, color: 'text-orange-400' },
    { id: 'multifamily', label: 'Multifamily', icon: Building, color: 'text-purple-400' },
  ];

  useEffect(() => { fetchDeals(activeTab); }, [activeTab]);

  const fetchDeals = async (dealType) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/deals/pipeline/${dealType}`);
      setDeals(response.data.properties || []);
    } catch (error) {
      console.error(error);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      <div className="border-b pb-4">
        <h1 className="text-2xl font-black uppercase tracking-tight">Active Pipelines</h1>
        <p className="text-xs font-mono text-muted-foreground uppercase">Real-time asset tracking & acquisition flow</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 h-12 bg-muted p-1 mb-8">
          {dealTypes.map((dt) => (
            <TabsTrigger key={dt.id} value={dt.id} className="gap-2 font-bold text-[10px] uppercase">
              <dt.icon size={14} /> {dt.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {dealTypes.map((dt) => (
          <TabsContent key={dt.id} value={dt.id} className="space-y-6">
            {loading ? (
              <div className="py-20 text-center animate-pulse text-sm font-bold uppercase tracking-widest">Scanning Sectors...</div>
            ) : deals.length === 0 ? (
              <div className="py-32 text-center opacity-30 border-4 border-dashed rounded-3xl">
                 <Bot size={48} className="mx-auto mb-4" />
                 <p className="text-lg font-bold uppercase italic">No active targets in this pipeline.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deals.map((deal, idx) => (
                  <Card key={idx} className="border-2 hover:border-primary transition-all overflow-hidden bg-card">
                    <CardHeader className="bg-muted/30 pb-4 border-b">
                       <CardTitle className="text-sm font-black truncate uppercase">{deal.address}</CardTitle>
                       <p className="text-[10px] font-mono text-muted-foreground">{deal.city}, {deal.state}</p>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                       <div className="flex justify-between items-end">
                          <p className="text-2xl font-black text-primary italic">${deal.price?.toLocaleString()}</p>
                          <Badge className="bg-green-500 font-mono text-[10px]">SCORE: {deal.deal_score || 85}</Badge>
                       </div>
                       <Button variant="outline" className="w-full h-10 border-2 font-black uppercase text-[10px] tracking-widest" onClick={() => navigate(`/property/${deal.id}`)}>
                          View Analysis Dossier
                       </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default DealPipeline;
